import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, GripVertical, Save } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const EMPTY = { year: "", title: "", note: "" };

export default function JourneyManager() {
  const [milestones, setMilestones] = useState([]);
  const [edits, setEdits] = useState({});
  const [creating, setCreating] = useState(EMPTY);
  const [savingId, setSavingId] = useState(null);
  const dragIndex = useRef(null);

  const load = async () => {
    const { data } = await api.get("/journey");
    setMilestones(data || []);
  };
  useEffect(() => { load(); }, []);

  const val = (m, k) => (edits[m.id]?.[k] !== undefined ? edits[m.id][k] : (m[k] ?? ""));
  const setField = (id, k, v) => setEdits((e) => ({ ...e, [id]: { ...(e[id] || {}), [k]: v } }));

  const saveOne = async (m) => {
    setSavingId(m.id);
    try {
      await api.put(`/journey/${m.id}`, {
        year: val(m, "year"),
        title: val(m, "title"),
        note: val(m, "note") || "",
      });
      toast.success("Saved.");
      setEdits((e) => { const n = { ...e }; delete n[m.id]; return n; });
      load();
    } catch {
      toast.error("Save failed.");
    } finally {
      setSavingId(null);
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this milestone?")) return;
    try {
      await api.delete(`/journey/${id}`);
      toast.success("Removed.");
      load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  const add = async () => {
    if (!creating.year.trim() || !creating.title.trim()) {
      toast.error("Year and Title are required.");
      return;
    }
    try {
      await api.post("/journey", creating);
      toast.success("Milestone added.");
      setCreating(EMPTY);
      load();
    } catch {
      toast.error("Failed to add.");
    }
  };

  const onDragStart = (i) => { dragIndex.current = i; };
  const onDragEnter = (i) => {
    const from = dragIndex.current;
    if (from === null || from === i) return;
    setMilestones((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(i, 0, moved);
      return next;
    });
    dragIndex.current = i;
  };
  const onDragEnd = async () => {
    dragIndex.current = null;
    try {
      await api.put("/journey/reorder", { ids: milestones.map((m) => m.id) });
      toast.success("Order saved.");
    } catch {
      toast.error("Failed to save order.");
      load();
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-journey-manager">
      <div className="border border-black/10 bg-white p-6">
        <p className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#4796d1] mb-1">About page</p>
        <h2 className="font-display font-semibold text-xl text-[#334155] mb-1">Our Journey — milestones</h2>
        <p className="text-sm text-[#52525B]">
          Add, edit, reorder and delete the milestones shown on the About page&apos;s horizontal timeline.
          The <b>last</b> milestone in this list appears as the &quot;latest&quot; highlight card.
        </p>
      </div>

      {/* Add new */}
      <div className="border border-black/10 bg-white p-6">
        <p className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#86b93f] mb-4">Add a milestone</p>
        <div className="grid md:grid-cols-6 gap-3 items-end">
          <div>
            <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">Year *</Label>
            <Input
              value={creating.year}
              onChange={(e) => setCreating({ ...creating, year: e.target.value })}
              className="mt-1 rounded-none border-black/15"
              placeholder="e.g. 2025"
              data-testid="admin-journey-new-year"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">Title *</Label>
            <Input
              value={creating.title}
              onChange={(e) => setCreating({ ...creating, title: e.target.value })}
              className="mt-1 rounded-none border-black/15"
              placeholder="e.g. Third factory opens"
              data-testid="admin-journey-new-title"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">Note</Label>
            <Input
              value={creating.note}
              onChange={(e) => setCreating({ ...creating, note: e.target.value })}
              className="mt-1 rounded-none border-black/15"
              placeholder="Short one-line description"
              data-testid="admin-journey-new-note"
            />
          </div>
          <button
            type="button"
            onClick={add}
            data-testid="admin-journey-add-btn"
            className="bg-[#4796d1] text-white h-11 flex items-center justify-center gap-2 font-mono-accent text-[10px] uppercase tracking-widest hover:bg-[#3a82ba]"
          >
            <Plus size={14} /> Add
          </button>
        </div>
      </div>

      {/* Existing */}
      <div className="border border-black/10 bg-white p-6">
        <div className="flex items-center justify-between mb-4">
          <p className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#4796d1]">
            {milestones.length} milestone{milestones.length !== 1 ? "s" : ""} — drag ⋮⋮ to reorder
          </p>
          <p className="font-mono-accent text-[10px] uppercase tracking-widest text-[#94A3B8]">Last row = latest</p>
        </div>
        <div className="space-y-3">
          {milestones.map((m, i) => {
            const dirty = !!edits[m.id];
            const isLatest = i === milestones.length - 1;
            return (
              <div
                key={m.id}
                draggable
                onDragStart={() => onDragStart(i)}
                onDragEnter={() => onDragEnter(i)}
                onDragOver={(e) => e.preventDefault()}
                onDragEnd={onDragEnd}
                data-testid={`admin-journey-row-${m.id}`}
                className={`grid grid-cols-[auto_100px_1fr_2fr_auto] items-start gap-3 border p-3 ${
                  dirty ? "border-[#86b93f]" : isLatest ? "border-[#4796d1]/50 bg-[#eaf6fc]/40" : "border-black/10 bg-white"
                }`}
              >
                <GripVertical size={16} className="text-[#94A3B8] cursor-grab active:cursor-grabbing shrink-0 mt-2.5" />
                <Input
                  value={val(m, "year")}
                  onChange={(e) => setField(m.id, "year", e.target.value)}
                  className="rounded-none border-black/15 font-mono-accent"
                  placeholder="Year"
                  data-testid={`admin-journey-year-${m.id}`}
                />
                <Input
                  value={val(m, "title")}
                  onChange={(e) => setField(m.id, "title", e.target.value)}
                  className="rounded-none border-black/15"
                  placeholder="Title"
                  data-testid={`admin-journey-title-${m.id}`}
                />
                <Textarea
                  value={val(m, "note")}
                  onChange={(e) => setField(m.id, "note", e.target.value)}
                  className="rounded-none border-black/15 min-h-[42px]"
                  placeholder="Short description"
                  data-testid={`admin-journey-note-${m.id}`}
                />
                <div className="flex items-center gap-1">
                  {dirty && (
                    <button
                      type="button"
                      onClick={() => saveOne(m)}
                      disabled={savingId === m.id}
                      data-testid={`admin-journey-save-${m.id}`}
                      className="w-9 h-9 flex items-center justify-center bg-[#86b93f] text-white hover:bg-[#6aa81f] disabled:opacity-50"
                    >
                      <Save size={14} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => remove(m.id)}
                    data-testid={`admin-journey-delete-${m.id}`}
                    className="w-9 h-9 flex items-center justify-center border border-black/15 text-[#52525B] hover:text-red-600 hover:border-red-400"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
          {milestones.length === 0 && (
            <p className="text-sm text-[#94A3B8] italic">No milestones yet — add one above.</p>
          )}
        </div>
      </div>
    </div>
  );
}
